import { useQuery } from '@apollo/client'
import { useMemo, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'

import { RANKINGS_QUESTIONS } from 'data/gradoo/queries/rankings'
import useAuth from 'data/gradoo/hooks/useAuth'
import useContentMarks, {
  ContentMarkType
} from 'shared/hooks/useContentMarks'
import { RankingsQuestionFragment } from 'shared/types/gradoo/graphql'
import Flex from 'shared/ui/Flex'
import SearchInput from 'shared/ui/SearchInput'

import Skeleton from './Skeleton'
import RankingsItem from './components/RankingsItem'

const PAGE_SIZE = 20
type RankingsPanelProps = {
  moduleInstanceId?: string
}

const RankingsPanel: React.FC<RankingsPanelProps> = ({
  moduleInstanceId
}) => {
  const [search, setSearch] = useState('')
  const { authGroupId } = useAuth()

  const { markedIds, toggleMark } = useContentMarks({
    groupId: authGroupId ?? undefined,
    moduleInstanceId
  })

  const {
    data: rankingsQuestionsData,
    loading,
    fetchMore
  } = useQuery(RANKINGS_QUESTIONS, {
    skip: !moduleInstanceId,
    variables: {
      moduleInstance: moduleInstanceId,
      first: PAGE_SIZE
    },
    notifyOnNetworkStatusChange: true,
    context: { client: 'gradoo' }
  })

  const rankingsQuestions: RankingsQuestionFragment[] =
    (rankingsQuestionsData?.rankingsQuestions?.edges.map(
      edge => edge?.node
    ) as RankingsQuestionFragment[]) || []

  const filteredRankingsQuestions = useMemo(() => {
    return rankingsQuestions.filter(rq =>
      rq.question
        .toLocaleLowerCase()
        .includes(search.toLocaleLowerCase())
    )
  }, [rankingsQuestions, search])

  const hasNextPage =
    rankingsQuestionsData?.rankingsQuestions?.pageInfo.hasNextPage ||
    false
  const endCursor =
    rankingsQuestionsData?.rankingsQuestions?.pageInfo.endCursor

  const onLoadMoreRankings = async () => {
    hasNextPage &&
      (await fetchMore({
        variables: {
          moduleInstance: moduleInstanceId,
          after: endCursor
        }
      }))
  }

  return (
    <Flex direction="column">
      <SearchInput
        value={search}
        onChange={setSearch}
        marginBottom={26}
      />
      {loading && rankingsQuestions.length === 0 ? (
        <Skeleton />
      ) : (
        <InfiniteScroll
          dataLength={rankingsQuestions.length}
          next={onLoadMoreRankings}
          hasMore={hasNextPage}
          loader={<Skeleton />}
          scrollThreshold={0.8}
          height={window.innerHeight}
        >
          <Flex direction="column" rowGap={22}>
            {filteredRankingsQuestions.map(rankingsQuestion => (
              <RankingsItem
                data-testid={`RankingsItem:${rankingsQuestion.id}`}
                key={rankingsQuestion.id}
                rankingsQuestion={rankingsQuestion}
                isMarked={markedIds.has(rankingsQuestion.id)}
                onToggleMark={() =>
                  toggleMark(
                    rankingsQuestion.id,
                    ContentMarkType.Ranking
                  )
                }
              />
            ))}
          </Flex>
        </InfiniteScroll>
      )}
    </Flex>
  )
}

export default RankingsPanel
